import { useCallback, useEffect } from 'react';
import { engRusLetterOrNumberOnlyRegex } from '@/core/utils/validation';
import { catchInsideWindowKeyDownEventClassName } from '@/warehouse/constants';
const catchKeyDownCodes = [
    // Enters
    'Enter',
    'NumpadEnter',
    // Pluses (`=/+` or `numpad +`)
    'Equal',
    'NumpadAdd',
    // Minuses
    'Minus',
    'NumpadSubtract',
    // Delete
    'Delete',
];
/**
 * Catches `onKeyDown` **window** based event and if the `key` is *alphabet* or *numeric* value, or the **Enter** key.
 * Then moves focus to specified input control and releases the key inside the control.
 *
 * Also, catches focus from elements with special className: {@link catchInsideWindowKeyDownEventClassName}
 */
/**
 * Catches `onKeyDown` **window** based event and if the `key` is *alphabet* or *numeric* value, or the **Enter** key.
 * Then moves focus to specified input control and releases the key in the control.
 *
 * Must not be used in multiple components on the same page if `disabled` prop is `true` in more than one hook
 * occurrences at one time.
 * The hook catches **body** events! For example, if there are multiple search fields on a page (*Inbox* and
 * *Verification* tabs) and both of them uses the hook with `disable = true`, then it will lead to errors (like
 * `Uncaught RangeError: Maximum call stack size exceeded.`)
 *
 * @param inputRef target input element that receives focus
 * @param disabled temporarily disables the hook
 */
const useCatchWindowFocus = (inputRef, disabled) => {
    const onWindowKeyDown = useCallback((event) => {
        // TODO: check if it should be removed, since we now add and remove event listener on component mount.
        // if (disabled) return;
        // Should NOT be used here, since it blocks `useOverrideWindowFindShortcut` hook from catching events (`Ctrl+F`
        // custom search).
        // event.stopImmediatePropagation();
        if (document.activeElement !== document.body &&
            !event.target?.classList.contains(catchInsideWindowKeyDownEventClassName)) {
            return;
        }
        // Ignore macos browser's refresh shortcut.
        if (event.key === 'r' && event.metaKey) {
            return;
        }
        const isLetterOrNumber = engRusLetterOrNumberOnlyRegex.test(event.key);
        if (isLetterOrNumber || catchKeyDownCodes.includes(event.code)) {
            inputRef.current?.focus();
            // TODO: this is not working, and, btw, fires `Uncaught RangeError: Maximum call stack size exceeded.`
            if (!isLetterOrNumber) {
                // Focused input field gets only chars inside it after window keydown event. If Enter or Ctrl+Key is pressed
                // event is not propagated to focused input. So dispatching same event on newly focused input is necessary.
                const eventDuplicate = new KeyboardEvent(event.type, event);
                inputRef.current?.dispatchEvent(eventDuplicate);
            }
        }
    }, [inputRef]);
    useEffect(() => {
        if (disabled) {
            removeEventListener('keydown', onWindowKeyDown);
        }
        else {
            addEventListener('keydown', onWindowKeyDown);
        }
    }, [disabled, onWindowKeyDown]);
    useEffect(() => {
        // Cleanup on unmount only
        return () => {
            removeEventListener('keydown', onWindowKeyDown);
        };
    }, [disabled, onWindowKeyDown]);
};
export { useCatchWindowFocus };
